import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "../lib/utils";
import { buttonVariants } from "./button";

function Calendar({
  className,
  classNames,
  modifiers,
  modifiersClassNames,
  showOutsideDays = true,
  ...props
}) {
  const effectiveWeekStartsOn =
    props.weekStartsOn ?? props.locale?.options?.weekStartsOn ?? 0;
  const isMondayStart = effectiveWeekStartsOn === 1;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("relative p-3", className)}
      modifiers={{
        weekend: { dayOfWeek: [0, 6] },
        ...modifiers,
      }}
      modifiersClassNames={{
        weekend:
          "text-primary [&_button]:text-primary [&_button]:font-semibold [&:not(:has([aria-selected]))_button]:bg-primary/[0.06] dark:[&:not(:has([aria-selected]))_button]:bg-primary/[0.12] [&[aria-selected=true]_button]:text-primary-foreground [&:has([aria-selected=true])_button]:text-primary-foreground",
        ...modifiersClassNames,
      }}
      classNames={{
        months:
          "relative flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center items-center h-9 px-9 relative",
        caption_label: "text-sm font-medium capitalize",
        nav: "flex items-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "absolute left-0 top-0 z-10 flex h-9 w-9 items-center justify-center bg-transparent p-0 opacity-70 hover:opacity-100 cursor-pointer",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "absolute right-0 top-0 z-10 flex h-9 w-9 items-center justify-center bg-transparent p-0 opacity-70 hover:opacity-100 cursor-pointer",
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex justify-center",
        weekday: cn(
          "text-muted-foreground rounded-md w-10 sm:w-9 font-normal text-[0.8rem] flex items-center justify-center capitalize",
          isMondayStart
            ? "[&:nth-child(6)]:text-primary [&:nth-child(6)]:font-semibold last:text-primary last:font-semibold"
            : "first:text-primary first:font-semibold last:text-primary last:font-semibold",
        ),
        week: "flex w-full justify-center mt-2",
        day: "h-10 w-10 sm:h-9 sm:w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 flex items-center justify-center",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "relative h-10 w-10 sm:h-9 sm:w-9 p-0 font-normal aria-selected:opacity-100 flex items-center justify-center after:absolute after:inset-[-2px] after:content-[''] cursor-pointer",
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary text-primary-foreground [&_button]:text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-primary aria-selected:text-primary-foreground aria-selected:opacity-100",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === "left") return <ChevronLeft className="h-4 w-4" />;
          return <ChevronRight className="h-4 w-4" />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
